import * as angular from 'angular';
import {AgentRequests} from "./agent-requests.service";
import {AgentsAlerts} from "./agents-alerts.service";
import {AgentModel} from "../angular/models/agent.model";
import {Document} from "../../commons/models/document.model";
import {ReminderSettings} from "../../site-preferences/models/reminder-settings.model";
import {Reminder} from "../../commons/models/reminder.model";
import {AnalyticsService} from "../../commons/services/analytics-service.service";
import {ILogService, IQService} from "angular";
import {AgentStatusType} from "../types/agentStatus.type";
import {AgentType} from "../types/agent.type";
import {BringozLoaderService} from "../../commons/services/bringoz-loader.service";
import {AgentsPaginationQueryModel} from "../angular/models/agents-pagination-query.model";
import {AccountModel} from "../../account/angular/models/account.model";
import {AgentAuthType} from "../types/agentAuth.type";
import {AgentClientType} from "../types/agentClient.type";
import * as _ from "lodash";
import {GpsPermissionType} from "../types/gps-permission.type";
import {BulkPreferencesUpdateQuery} from "../models/queries.model";

export class AgentsService {

  constructor(private agentRequests: AgentRequests, private agentsAlerts: AgentsAlerts,
              private $q: IQService, private bringozLoaderService: BringozLoaderService,
              private $log: ILogService, private analyticsService: AnalyticsService,
              private agentStatus) {
  }

  getAll(options) {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.getAll(options)
        .then((updatedAgent) => {
          resolve(updatedAgent);
        })
        .catch((error) => {
          this.$log.error(error);
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  getAgentsPagination(queryPayload: AgentsPaginationQueryModel) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.getAgentsPagination(queryPayload)
      .then((res) => {
        // this.agentsAlerts.updateBillingAccountSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        // this.agentsAlerts.updateBillingAccountError();
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  createOne(agent: AgentModel): Promise<AgentModel> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.createOne(agent)
        .then((updatedAgent) => {
          this.agentsAlerts.createSuccess();
          resolve(updatedAgent as AgentModel);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.createError(error);
          this.analyticsService.trackEvent('Agents Actions', 'Create', 'Error');
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  assignConfigurationProfile(profileId: string, agentIds: string[]) {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.assignAgentsToProfile(profileId, agentIds)
        .then((agents: AgentModel[]) => {
          this.agentsAlerts.updateSuccess();
          resolve(agents);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.updateError(error);
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  getAgentTotalsByConfigurationId(configurationId: string) {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.getAgentTotalsByConfigurationId(configurationId)
        .then((agents: AgentModel[]) => {
          resolve(agents);
        })
        .catch((error) => {
          this.agentsAlerts.updateError(error);
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  updateOne(agentId: string, agent: AgentModel): Promise<AgentModel> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.updateOne(agentId, agent)
        .then((updatedAgent) => {
          this.agentsAlerts.updateSuccess();
          resolve(updatedAgent as AgentModel);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.updateError(error);
          this.analyticsService.trackEvent('Agents Actions', 'Update', 'Error');
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  updateCarrier(agentId: string, carrierId: string): Promise<AgentModel> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.updateCarrier(agentId, carrierId)
        .then((updatedAgent) => {
          this.agentsAlerts.updateSuccess();
          resolve(updatedAgent as AgentModel);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.updateError(error);
          this.analyticsService.trackEvent('Agents Actions', 'Update', 'Error');
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  updatePassword(agentId: string, password: string): Promise<AgentModel> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.updatePassword(agentId, password)
        .then((updatedAgent) => {
          this.agentsAlerts.updatePasswordSuccess();
          resolve(updatedAgent as AgentModel);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.updatePasswordError();
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  selectScheduleTemplate(agentId: string, scheduleTemplateId?: string) {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.selectScheduleTemplate(agentId, scheduleTemplateId)
        .then((agent) => {
          this.agentsAlerts.selectScheduleTemplateSuccess();
          resolve(agent);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.selectScheduleTemplateError();
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  clearSchedule(agentId: string) {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.clearSchedule(agentId)
        .then((schedule) => {
          this.agentsAlerts.clearScheduleSuccess();
          resolve(schedule);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.clearScheduleError();
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  checkEditItinerary(agentId: string, taskIds: string[]) {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.checkEditItinerary(agentId, taskIds)
        .then((schedule) => {
          resolve(schedule);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.updateItineraryError();
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  saveEditItinerary(agentId: string, taskIds: string[]) {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.saveEditItinerary(agentId, taskIds)
        .then((schedule) => {
          this.agentsAlerts.updateItinerarySuccess();
          resolve(schedule);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.updateItineraryError();
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  resetItinerary(agentId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.editItineraryReset(agentId)
        .then((schedule) => {
          this.agentsAlerts.updateItinerarySuccess();
          resolve(schedule);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.updateItineraryError();
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  editItinerarySettings(agentId: string, itinerary): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.editItinerarySettings(agentId, itinerary)
        .then((itinerary) => {
          this.agentsAlerts.updateItinerarySuccess();
          resolve(itinerary);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.updateItineraryError();
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  getAgentBalance(agentId: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.getAgentBalance(agentId)
        .then((balance) => {
          resolve(balance);
        })
        .catch((error) => {
          this.$log.error(error);
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  getAgentPaymentCollectionTotals(agentId: string, from: number, to: number, transactionType: any, accountId?: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.getAgentPaymentCollectionTotals(agentId, from, to, transactionType, accountId)
        .then((totals) => {
          resolve(totals);
        })
        .catch((error) => {
          this.$log.error(error);
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  getAllAgentsPaymentTotals(): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.getAllAgentsPaymentTotals()
        .then((totals) => {
          resolve(totals);
        })
        .catch((error) => {
          this.$log.error(error);
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  addPaymentTransaction(agentId: string, transaction): Promise<any> {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.addPaymentTransaction(agentId, transaction)
        .then((transaction) => {
          resolve(transaction);
        })
        .catch((error) => {
          this.$log.error(error);
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  bulkPreferencesUpdate(preferences: any, query: BulkPreferencesUpdateQuery) {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.bulkPreferencesUpdate(preferences, query)
        .then((res) => {
          this.agentsAlerts.bulkPreferencesUpdateInfo();
          resolve(res);
        })
        .catch((error) => {
          this.agentsAlerts.bulkPreferencesUpdateError();
          this.$log.error(error);
          reject(error)
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    })
  }

  sendNotification(agentPnType: string, selectedIds: string[], excludedIds?: string[], branchIds?: string[], noBranches?: boolean, status?: AgentStatusType, agentType?: AgentType,
                   agentSkills?: string [], gpsStatus?: boolean, gpsPermissions?: string[], isActive?: boolean, sessionRecording?: boolean, noArea?: boolean, serviceAreaIds?: string[]) {

    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.sendNotification(agentPnType, selectedIds, excludedIds, branchIds, noBranches, status, agentType, agentSkills, gpsStatus, gpsPermissions, isActive, sessionRecording, noArea, serviceAreaIds)
      .then((res) => {
        if (agentPnType === 'ClockInUpdate') {
          this.agentsAlerts.agentClockInNotificationSuccess();
        } else {
          this.agentsAlerts.agentGpsNotificationSuccess();
        }
        deferred.resolve(res);
      })
      .catch((error) => {
        if (agentPnType === 'ClockInUpdate') {
          this.agentsAlerts.agentClockInNotificationError();
        } else {
          this.agentsAlerts.agentGpsNotificationError();
        }
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise


  }

  updateAgentAssistants(agent: AgentModel) {
    const deferred = this.$q.defer<AgentModel>();

    this.bringozLoaderService.on();
    this.agentRequests.updateAgentAssistants(agent)
      .then((res: AgentModel) => {
        this.agentsAlerts.updateAssistantsSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.agentsAlerts.updateAssistantsError();
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  getAgentAssistants(agentId: string) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.getAgentAssistants(agentId)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.agentsAlerts.getAssistantsError();
        this.$log.error(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  getAgentLocation(agentId: string) {
    const deferred = this.$q.defer<any>();

    this.agentRequests.getAgentLocation(agentId)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.agentsAlerts.getAssistantsError();
        this.$log.error(error);
        deferred.reject(error)
      })

    return deferred.promise
  }

  getAgentPreferences(agentId: string) {
    const deferred = this.$q.defer();

    this.agentRequests.getAgentPreferences(agentId)
      .then((res) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        deferred.reject(error)
      })

    return deferred.promise
  }

  exportAgents(exportType, status, agentType: string,  branchIds, noBranches, isActive: boolean, sessionRecording: boolean, areaIds, gpsStatus, gpsPermissions) {
    return new Promise((resolve, reject) => {
      this.bringozLoaderService.on();
      this.agentRequests.exportAgents(exportType, status, agentType, branchIds, noBranches, isActive, sessionRecording, areaIds, gpsStatus, gpsPermissions)
        .then((updatedAgent) => {
          this.agentsAlerts.exportSuccess();
          resolve(updatedAgent);
        })
        .catch((error) => {
          this.$log.error(error);
          this.agentsAlerts.exportError();
          reject(error);
        })
        .finally(() => {
          this.bringozLoaderService.off();
        });
    });
  }

  //////////////////////////////////////// DOCUMENTS ////////////////////////////////////////

  getDocuments(agentId: string) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.getDocuments(agentId)
      .then((documents: Document[]) => {
        deferred.resolve(documents);
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.getDocumentsError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  createDocument(document: any, agentId) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.createDocument(document, agentId)
      .then((createdDocument: Document) => {
        this.agentsAlerts.createDocumentSuccess();
        deferred.resolve(createdDocument);
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.createDocumentError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  updateDocument(document: Document, agentId: string) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.updateDocument(document, agentId)
      .then((document: Document) => {
        this.agentsAlerts.updateDocumentSuccess();
        deferred.resolve(document);
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.updateDocumentError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  deleteDocument(documentId: string, agentId: string) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.deleteDocument(documentId, agentId)
      .then(() => {
        this.agentsAlerts.deleteDocumentSuccess();
        deferred.resolve();
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.deleteDocumentError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });
    return deferred.promise;
  }

  //////////////////////////////////////// REMINDERS ////////////////////////////////////////

  getReminders(agentId: string) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.getReminders(agentId)
      .then((reminders: ReminderSettings[]) => {
        deferred.resolve(reminders);
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.getRemindersError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  createReminder(reminder: Reminder, agentId: string) {
    const deferred = this.$q.defer<Reminder>();

    this.bringozLoaderService.on();
    this.agentRequests.createReminder(reminder, agentId)
      .then((createdReminder: Reminder) => {
        this.agentsAlerts.createReminderSuccess();
        deferred.resolve(createdReminder);
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.createReminderError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  updateReminder(reminder: Reminder, agentId: string) {
    const deferred = this.$q.defer<Reminder>();

    this.bringozLoaderService.on();
    this.agentRequests.updateReminder(reminder, agentId)
      .then((updatedReminder: Reminder) => {
        this.agentsAlerts.updateReminderSuccess();
        deferred.resolve(updatedReminder);
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.updateReminderError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  changeArchiveStatus(agentId: string, agent: AgentModel) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.updateOne(agentId, agent)
      .then(() => {
        this.agentsAlerts.changeArchiveStatusSuccess();
        deferred.resolve();
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.changeArchiveStatusError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  deleteReminder(reminderId: string, agentId: string) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.deleteReminder(reminderId, agentId)
      .then(() => {
        this.agentsAlerts.deleteReminderSuccess();
        deferred.resolve();
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.deleteReminderError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  passwordless(agentId: string, authType: AgentAuthType, client: AgentClientType) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.passwordless(agentId, authType, client)
      .then(() => {
        this.agentsAlerts.passwordlessSuccess(authType);
        deferred.resolve();
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.passwordlessError(authType);
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  addDepots(depotIds: string[], agentIds: string[]) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.addDepots(depotIds, agentIds)
      .then((res: AgentModel[]) => {
        this.agentsAlerts.addToDepotSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.addToDepotSuccess();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  addBranches(branchIds: string[], agentIds: string[]) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.addBranches(branchIds, agentIds)
      .then((res: AgentModel[]) => {
        this.agentsAlerts.addToBranchSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.$log.error(error);
        this.agentsAlerts.addToBranchError();
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise;
  }

  getAgentPerformance(agentId: string, from: number, to: number) {
    const deferred = this.$q.defer();

    this.agentRequests.getAgentPerformance(agentId, from, to)
      .then((res: AgentModel[]) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        deferred.reject(error)
      })

    return deferred.promise;
  }

  changeAgentStatus(status, agentId) {
    const deferred = this.$q.defer();

    this.agentRequests.changeAgentsStatus(status, [agentId])
      .then((res) => {

        if (_.get(res, '[0].success')) {
          this.agentsAlerts.updateStatusSuccess(this.agentStatus.statusToTitle[status]);
        } else {
          this.agentsAlerts.updateStatusInfo();
        }
        this.analyticsService.trackEvent('Agents Actions', 'Change Status', status);
        deferred.resolve(res);
      })
      .catch((error) => {
        this.agentsAlerts.updateStatusError();
        this.$log.error('Failed to change agent status, agentStatus@changeStatus \n' + error);
        this.analyticsService.trackEvent('Agents Actions', 'Change Status', 'Error');
        deferred.reject(error)
      })

    return deferred.promise;

  }

  updateAgentExternalId(agent: AgentModel) {
    const deferred = this.$q.defer<AgentModel>();

    this.bringozLoaderService.on();
    this.agentRequests.updateAgentExternalId(agent)
      .then((res: AgentModel) => {
        this.agentsAlerts.updateSuccess();
        // this.agentsAlerts.updateAssistantsSuccess();
        deferred.resolve(res);
      })
      .catch((error) => {
        this.agentsAlerts.updateError(error);
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  addAgentsToTeam(teamIds: string[], agentIds: string[]) {
    const deferred = this.$q.defer<AgentModel>();

    this.bringozLoaderService.on();
    this.agentRequests.addAgentsToTeam(teamIds, agentIds)
      .then((res: AgentModel) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  removeAgentsFromTeam(teamIds: string[], agentIds: string[]) {
    const deferred = this.$q.defer<AgentModel>();

    this.bringozLoaderService.on();
    this.agentRequests.removeAgentsFromTeam(teamIds, agentIds)
      .then((res: AgentModel) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  getAgentCrossSiteData(email: string, id: string) {
    const deferred = this.$q.defer();

    this.bringozLoaderService.on();
    this.agentRequests.getAgentCrossSiteData(email,id)
      .then((res: any) => {
        deferred.resolve(res);
      })
      .catch((error) => {
        deferred.reject(error)
      })
      .finally(() => {
        this.bringozLoaderService.off();
      });

    return deferred.promise
  }

  doesAgentHasGpsPermissions(agent: AgentModel) {
    return agent.locationTrackingApproval;
    // return (!!agent.lastKnownLocation?.deviceReference?.gpsPermission &&
    //   agent.lastKnownLocation.deviceReference.gpsPermission === GpsPermissionType.All);
  }


}

angular
  .module('bringoz.admin.agents')
  .service('agentsService', AgentsService);
